import { FormGroupLabel, FormGroupWrapper } from "./FormGroup";
import React, { forwardRef, useEffect, useState } from "react";

import CheckBox from "./CheckBox";
import PropTypes from "prop-types";
import eyeClosed from "assets/icons/eyeClosed.svg";
import eyeIcon from "assets/icons/eye.svg";
import styled from "styled-components";
import Loader from "./Loader";
import DateFilterWrapper from "views/postPlanner/posts/DateFilterWrapper";
import ReactDatePicker from "react-datepicker";

const CheckboxField = styled.div``;

const handleToggleShowPassword = (e, id) => {
  const field = document.querySelector(`#${id}`);

  if (field.type === "password") {
    field.type = "text";
  } else {
    field.type = "password";
  }
};

const handleCheckboxSelect = (e, id) => {
  e.preventDefault();

  const selectedElCheckbox = document.querySelector(`#${id}`);
  selectedElCheckbox.click();
};

// @ts-ignore
const DateFilter = forwardRef(({ value, onClick }, ref) => (
  <button type="button" style={{ width: "100%" }} onClick={onClick} ref={ref}>
    {value ? (
      <span className="flexRow alignCenter l3" style={{ gap: "8px" }}>
        {value}
      </span>
    ) : (
      <>
        <span className="l3 flexRow alignCenter colorBodyText">MM/DD/YYY</span>
      </>
    )}
  </button>
));

const ExampleCustomTimeInput = ({ value, onChange }) => (
  <input
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onClick={(e) => e.target?.focus()}
    style={{ border: "solid 1px pink" }}
  />
);

/**
 * Custom form group component.
 *
 * @component
 * @param {object} props - The component props.
 * @param {string} [props.className] - Additional class name for the component.
 * @param {string} props.fieldStyle - Style of the form field. Can be "shortText" or "longText".
 * @param {string} [props.type="text"] - Type of the input field. Defaults to "text".
 * @param {string} props.name - Name of the input field.
 * @param {string} [props.label] - Label for the input field.
 * @param {boolean} [props.required] - Indicates if the input field is required.
 * @param {string} [props.value] - Value of the input field.
 * @param {string} [props.color] - Color of the input field.
 * @param {string} [props.error] - Error message for the input field.
 * @param {function} [props.setValue] - Callback function to set the value of the input field.
 * @param {boolean} [props.loading=false] - Indicates if the input field is in a loading state.
 * @returns {JSX.Element} - The rendered form group component.
 */
const FormGroupCustom = ({
  className,
  fieldStyle,
  type = "text",
  name,
  label,
  required,
  value,
  color,
  error,
  setValue,
  loading = false,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.querySelectorAll(`input[type="number"]`).forEach((input) => {
      input.addEventListener("mousewheel", function (e) {
        e.target.blur();
      });
    });
    // eslint-disable-next-line
  }, []);

  if (type === "checkbox") {
    return (
      <CheckboxField className={`flex-row align-center ${className ?? ""}`}>
        <CheckBox className="checkbox" id={name} name={name} />
        <FormGroupLabel
          className="cursorPointer"
          htmlFor={name}
          onClick={(e) => handleCheckboxSelect(e, name)}
          style={{ marginBottom: 0, marginLeft: "0.5rem" }}
        >
          {label}
        </FormGroupLabel>
      </CheckboxField>
    );
  }

  if (type === "date") {
    return (
      <FormGroupWrapper className={className} fieldStyle={fieldStyle}>
        {label && (
          <FormGroupLabel htmlFor={name}>
            {label}
            {required && <sup>*</sup>}
          </FormGroupLabel>
        )}
        <div className="fieldWrapper">
          <DateFilterWrapper className="dateFilterWrapper">
            <ReactDatePicker
              dateFormat="yyyy-MM-dd"
              selected={value}
              onChange={(date) => setValue(date)}
              customInput={<DateFilter />}
              calendarClassName="dateFilterCalendar"
              showTimeInput
            />
          </DateFilterWrapper>
        </div>
        <span className="l3 colorDanger errorText">{error}</span>
      </FormGroupWrapper>
    );
  }

  return (
    <FormGroupWrapper className={className} fieldStyle={fieldStyle}>
      {label && (
        <FormGroupLabel htmlFor={name}>
          {label}
          {required && <sup>*</sup>}
        </FormGroupLabel>
      )}
      <div className="fieldWrapper">
        {fieldStyle === "shortText" && (
          <>
            <input
              className="textSmall"
              id={name}
              type={type}
              color={color}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              {...props}
              autoComplete="off"
            />
            {type === "password" && (
              <img
                src={showPassword ? eyeClosed : eyeIcon}
                alt="eyeIcon"
                className="toggleShow cursorPointer"
                onClick={(e) => {
                  setShowPassword((prev) => !prev);
                  handleToggleShowPassword(name);
                }}
              />
            )}
            {loading && (
              <div className="loaderWrapper">
                <Loader type="spinner" />
              </div>
            )}
          </>
        )}
        {fieldStyle === "longText" && (
          <>
            <textarea
              className="textSmall"
              id={name}
              name={name}
              required={required}
              value={value}
              {...props}
              autoComplete="off"
            />
          </>
        )}
      </div>

      <span className="l3 colorDanger errorText">{error}</span>
    </FormGroupWrapper>
  );
};

FormGroupCustom.propTypes = {
  className: PropTypes.string,
  fieldStyle: PropTypes.string.isRequired,
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  color: PropTypes.string,
  error: PropTypes.string,
};

export default FormGroupCustom;
